import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, SIGNED_OUT_EVENT } from './api';
import type { Role, User } from './types';

export type Permission = 'edit' | 'comment' | 'review' | 'admin';

const PERMISSIONS: Record<Permission, Role[]> = {
  edit: ['admin', 'editor'],
  comment: ['admin', 'editor', 'reviewer'],
  review: ['admin', 'reviewer'],
  admin: ['admin'],
};

type AuthApi = {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (permission: Permission) => boolean;
};

const AuthContext = createContext<AuthApi>({
  user: null, loading: true, signIn: async () => {}, signOut: async () => {}, can: () => false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.me().then(setUser).catch(() => setUser(null)).finally(() => setLoading(false));
    const onSignedOut = () => setUser(null);
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    setUser(await api.login(username, password));
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
  }, []);

  const can = useCallback((permission: Permission) => !!user && PERMISSIONS[permission].includes(user.role), [user]);

  const value = useMemo(() => ({ user, loading, signIn, signOut, can }), [user, loading, signIn, signOut, can]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
